import { useEffect, useRef } from 'react';
import { useStore } from '@betgames/bg-state-manager';
import { useAfterUpdate } from '@betgames/bg-tools';
import { GameId } from '@bg-shared/enums';
import { gamesOptionsEntity, gamesOptionsSelectors } from '@bg-shared/business/GamesOptions';
import { gamesStatusEntity, gamesStatusSelectors } from '@bg-shared/business/GamesStatus';
import { recentBetsStore, recentBetsSelectors } from '@bg-shared/business/RecentBets';
import { playerWonEvents } from '@bg-shared/business/PlayerWon';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { casinoLatestPayinEntity } from '../store';

export const useSyncActivePayinBets = (gameId: GameId) => {
    const gameStarted = useStore(gamesStatusEntity.store(gameId), gamesStatusSelectors.getStarted);
    const activeBets = useStore(recentBetsStore, recentBetsSelectors.getActiveBetsByGameId(gameId));
    const previousBetIdsRef = useRef<Set<number>>(new Set());

    useAfterUpdate(() => {
        if (!activeBets.length) {
            previousBetIdsRef.current.clear();
            return;
        }

        const newBets = activeBets.filter((bet) => !previousBetIdsRef.current.has(bet.id));

        if (!newBets.length) {
            return;
        }

        newBets.forEach((bet) => previousBetIdsRef.current.add(bet.id));

        const gameItems = gamesOptionsSelectors.getItems(gamesOptionsEntity.store(gameId).value);

        const formattedBets = newBets.map((bet) => ({
            id: bet.id,
            amount: Number(bet.amountBet),
            oddId: bet.oddsId,
            oddClass: bet.oddsClass,
            oddValue: Number(bet.oddsValue),
            items: bet.betData?.items?.map((item) => {
                const matchingLotteryItem = gameItems.find(
                    (lotteryItem) => lotteryItem.number === item.number,
                );
                return matchingLotteryItem ? matchingLotteryItem.id : null;
            }),
        }));

        /*
        We should store new bets in the latest payin entity only if the game has started.
        We need old bets for "repeat" functionality
         */
        if (gamesStatusEntity.store(gameId).value.started) {
            casinoLatestPayinEntity.store(gameId).addBets(newBets[0].runId, formattedBets);
        } else {
            casinoLatestPayinEntity.store(gameId).addCurrentBets(newBets[0].runId, formattedBets);
        }
    }, [activeBets.length]);

    useAfterUpdate(() => {
        if (gameStarted && casinoLatestPayinEntity.store(gameId).value.currentBetting.bets.length) {
            casinoLatestPayinEntity.store(gameId).switchCurrentBetsToRecent();
        }
    }, [gameStarted]);

    useEffect(() => {
        return playerWonEvents.subscribe(gameId, (data) => {
            setTimeout(() => {
                casinoLatestPayinEntity.store(gameId).recentWin = data.amountWon;
            }, partnerSettingsStore.getGameDelay(gameId));
        });
    }, []);
};
